<template>
    <div>
        <div class="d-flex align-center mb-1">
            <v-text-field
                v-model.trim="search_query"
                :label="$t('components.orders_closed_list.search_label')"
                prepend-inner-icon="mdi-magnify"
                clearable
            ></v-text-field>

            <v-spacer></v-spacer>

            <span class="body-2 text--secondary d-none d-sm-inline">
            {{ from }}-{{ to }} {{ $t('components.orders_closed_list.pagination_of') }} {{ total }}
        </span>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="is_page_first" @click="prev_page">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('components.orders_closed_list.pagination_prev_page') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="is_page_last" @click="next_page">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('components.orders_closed_list.pagination_next_page') }}</span>
            </v-tooltip>
        </div>

        <loading-message v-if="loading" :message="loading_message" class="my-5"></loading-message>

        <v-simple-table v-else-if="orders.length">
            <template v-slot:default>
                <tbody>
                <router-link
                    :to="{name: 'order_closed_show', params: { id: order.ClosedOrderID }}"
                    tag="tr"
                    v-for="order in orders"
                    :key="order.OrderName"
                    style="cursor: pointer"
                >
                    <th class="text-no-wrap">
                        {{ order.OrderName }}
                        <template v-if="order.CustomerName">
                            <div class="font-weight-regular text-caption text--secondary">
                                {{order.CustomerName}}
                            </div>
                        </template>
                    </th>
                    <td>
                        <v-chip v-for="system in order.Systems" :key="'system'+system.SystemID" x-small color="info" class="my-1 mr-1">{{ system.SystemName }}</v-chip>
                    </td>
                    <td class="text-no-wrap"><!-- {{ order.Price | currencyFormat }} --></td>
                    <td>
                        <order-status-text
                            :order-status="order.OrderStatus"
                            :order-status-name="order.OrderStatusName"
                        ></order-status-text>
                    </td>
                    <td class="text-no-wrap">{{ order.OrderDate | dateConvenientFormat }}</td>
                    <td class="text-right">

                        <v-menu
                            bottom
                            left
                            :offset-y="true"
                            rounded
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    small
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>

                            <v-list dense>
                                <v-list-item>
                                    <v-list-item-title>
                                        <router-link :to="{name: 'order_closed_show', params: { id: order.ClosedOrderID }}">
                                            <v-icon small>mdi-folder-search-outline</v-icon>
                                            {{ $t('components.orders_closed_list.details') }}
                                        </router-link>
                                    </v-list-item-title>
                                </v-list-item>

                            </v-list>
                        </v-menu>

                    </td>
                </router-link>
                </tbody>
            </template>
        </v-simple-table>

        <v-alert
            v-else
            border="left"
            colored-border
            type="info"
            elevation="1"
        >{{ empty_message }}</v-alert>
    </div>
</template>

<script>
import LoadingMessage from "./LoadingMessage";
import OrderStatusText from "./OrderStatusText";

export default {
    name: "OrdersClosedList",

    components: {
        LoadingMessage,
        OrderStatusText,
    },

    data() {
        return {
            search_query: "",
            source: null,

            loading: false,

            orders: [],

            // pagination
            page: 1,
            last_page:1,
            from: 0,
            to: 0,
            total: 0,
        }
    },

    computed: {
        is_mobile() {
            return this.$vuetify.breakpoint.mobile;
        },

        is_page_first() {
            return this.page === 1;
        },

        is_page_last() {
            return this.page === this.last_page;
        },

        loading_message() {
            return ! this.search_query
                ? this.$t('components.orders_closed_list.loading_orders')
                : this.$t('components.orders_closed_list.searching_orders');
        },

        empty_message() {
            return ! this.search_query
                ? this.$t('components.orders_closed_list.no_orders')
                : this.$t('components.orders_closed_list.no_orders_matching_query');
        },
        locale: function () {
            return this.$i18n.locale;
        },
    },

    methods: {
        next_page: function () {
            if(this.page < this.last_page) this.page++;
        },
        prev_page: function () {
            if(this.page > 1) this.page--;
        },

        fetch: function () {
            this.loading = true;

            this.$store.dispatch('orderClosedIndex', {
                page: this.page,
                search_query: this.search_query
            }).then(response => {
                if(response.status === 200) {
                    this.orders = response.data.data;
                    this.page = response.data.meta.current_page ?? 1;
                    this.last_page = response.data.meta.last_page ?? 1;
                    this.from = response.data.meta.from ?? 0;
                    this.to = response.data.meta.to ?? 0;
                    this.total = response.data.meta.total;
                } else {
                    this.$store.commit(
                        "showSnackbarError",
                        this.$t('components.orders_closed_list.loading_orders_failed')
                    );
                }
            })
                .catch(() => {})
                .then(() => {
                    this.loading = false;
                });
        },
    },

    created() {
        this.debouncedFetch = _.debounce(this.fetch, 900);
        this.debouncedFetchFast = _.debounce(this.fetch, 300);

        this.fetch();
    },

    watch: {
        search_query: function () {
            //If any request_search is currently in progress, cancel it!
            if(this.$store.state.sources.order_closed_index) {
                this.$store.state.sources.order_closed_index.cancel();
            }

            //Run debouncedFetch
            this.debouncedFetch();
        },
        page: function () {
            //If any request_search is currently in progress, cancel it!
            if(this.$store.state.sources.order_closed_index) {
                this.$store.state.sources.order_closed_index.cancel();
            }

            //Run debouncedFetch
            this.debouncedFetchFast();
        },
        locale() {
            this.fetch();
        },
    }
}
</script>

<style scoped>

</style>
